import React from 'react';

import styled from 'styled-components';

// constants
import aampeTheme from '../../../constants/aampeTheme';
import ICONS from '../../../constants/icons';

import { createShouldForwardProp } from 'helpers/shouldForwardProp';

interface BasicChipProps {
    className?: string;
    title: string;
    onClickHandler?: () => void;
    onTagClick?: () => void;
    style?: React.CSSProperties;
    iconStyle?: React.CSSProperties;
    icon?: React.ReactElement;
    chipTheme?: 'default' | 'beta' | 'event';
}
interface TagContainerProps {
    tagClick: boolean;
    chipTheme: 'default' | 'beta' | 'event';
}
const shouldForwardTagProps = createShouldForwardProp([
    'tagClick',
    'chipTheme',
]);

const BasicChip: React.FC<React.PropsWithChildren<BasicChipProps>> = ({
    className,
    title,
    onClickHandler,
    onTagClick,
    style,
    iconStyle,
    icon,
    chipTheme = 'default',
}) => {
    return (
        <>
            {title && (
                <Container
                    className={className}
                    style={style}
                    onClick={onTagClick}
                    tagClick={!!onTagClick}
                    chipTheme={chipTheme}
                >
                    {icon}
                    <Title>{title}</Title>
                    {onClickHandler && (
                        <div
                            onClick={onClickHandler}
                            style={{ cursor: 'pointer' }}
                        >
                            <Icon style={iconStyle} />
                        </div>
                    )}
                </Container>
            )}
        </>
    );
};

const Container = styled.div.withConfig({
    shouldForwardProp: shouldForwardTagProps,
})<TagContainerProps>((props) => {
    // Define theme styles
    const themeStyles: Record<
        'default' | 'beta' | 'event',
        React.CSSProperties
    > = {
        default: {
            backgroundColor: aampeTheme.colors.backgroundWG01,
            border: `1px solid ${aampeTheme.colors.interactiveWG01}`,
            color: aampeTheme.colors.neutral09,
            lineHeight: aampeTheme.size.lineHeight.textSmall,
        },
        beta: {
            backgroundColor: aampeTheme.colors.textAccessibleWG01,
            color: aampeTheme.colors.neutral09,
            fontSize: aampeTheme.size.font.textSmall,
            borderRadius: aampeTheme.spacing4(2),
            padding: aampeTheme.spacing4(0, 1),
            lineHeight: aampeTheme.size.lineHeight.default,
            height: aampeTheme.spacing4(6),
        },
        event: {
            backgroundColor: aampeTheme.colors.white,
            color: aampeTheme.colors.neutral09,
            fontSize: aampeTheme.size.font.textSmall,
            borderRadius: aampeTheme.borderRadius.small,
            padding: aampeTheme.spacing4(2, 3),
            height: 'auto',
        },
    };

    return {
        display: 'inline-flex',
        alignItems: 'center',
        borderRadius: aampeTheme.borderRadius.message,
        cursor: props.tagClick ? 'pointer' : 'default',
        padding: aampeTheme.spacing4(1, 2, 1, 3),
        gap: aampeTheme.spacing4(1),
        height: '32px',
        ...themeStyles[props.chipTheme],
    };
});

const Title = styled.span({
    fontSize: aampeTheme.size.font.textMedium,
    fontWeight: aampeTheme.weights.light,
    color: 'inherit',
});

const Icon = styled(ICONS.close)(() => ({
    height: '20px',
    display: 'block',
    alignItems: 'center',
    cursor: 'pointer',
    color: aampeTheme.colors.textAccessibleWG01,
}));

export default BasicChip;
